import React, { useState, useEffect } from 'react';
import axios from '../Axiosinstance';
import { decryptData } from '../Axiosinstance';
import { useNavigate } from 'react-router-dom';
import Head from './Headstu';
import { toast } from 'react-toastify';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Button,
    Divider,
    Stack,
    CircularProgress,
    useTheme
} from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SendIcon from '@mui/icons-material/Send';
import Rating from '@mui/material/Rating';

const FeedbackForm = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ratings, setRatings] = useState({});
    const [hasSubmitted, setHasSubmitted] = useState(false);


    const theme = useTheme();
    const navigate = useNavigate();
    const studentid = decryptData(localStorage.getItem("studentteacherid"));

    useEffect(() => {
        const checkFeedbackSubmission = async () => {
            try {
                // First check if student has already submitted feedback
                const checkResponse = await axios.get(`/api/feedback/check-submitted/${studentid}`);
                setHasSubmitted(checkResponse.data);

                // Only load questions if student hasn't submitted yet
                if (!checkResponse.data) {
                    const questionsResponse = await axios.get('/feedback-questions');
                    setQuestions(questionsResponse.data);
                    // Initialize ratings
                    const initialRatings = {};
                    questionsResponse.data.forEach(q => initialRatings[q.id] = null);
                    setRatings(initialRatings);
                }
            } catch (err) {

                console.error("Failed to load feedback data:", err);
            } finally {
                setLoading(false);

            }
        };

        checkFeedbackSubmission();
    }, [studentid]);

    const getRatingLabel = (value) => {
        switch (value) {
            case 1: return "Strongly Dissatisfied";
            case 2: return "Dissatisfied";
            case 3: return "Neutral";
            case 4: return "Satisfied";
            case 5: return "Very Satisfied";
            default: return "Select rating";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const answers = Object.entries(ratings).map(([questionId, value]) => ({
            questionId: parseInt(questionId),
            answerText: getRatingLabel(value)
        }));

        try {
            await axios.post('/api/feedback/submit', {
                studentId: studentid,
                answers
            });
            toast.success('Thank you for your feedback!');

            navigate('/Dashboard');

        } catch (err) {
            toast.error('Submission failed. Please try again.');
        }
    };

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                <Box textAlign="center">
                    <CircularProgress size={60} />
                    <Typography variant="h6" mt={2}>
                        {hasSubmitted ? 'Checking feedback status...' : 'Loading feedback form...'}
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (hasSubmitted) {
        return (
            <>
                <Head />
                <Container maxWidth="md" sx={{
                    py: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-50%',
                        left: '-50%',
                        width: '200%',
                        height: '200%',
                        background: theme.palette.mode === 'dark'
                            ? 'radial-gradient(circle, rgba(74,108,247,0.1) 0%, transparent 70%)'
                            : 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)',
                        animation: 'liquidExpand 4s ease-in-out infinite alternate',
                        zIndex: -1
                    }
                }}>
                    {/* Blob background elements */}
                    <Box sx={{
                        position: 'absolute',
                        top: '20%',
                        right: '10%',
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: theme.palette.mode === 'dark'
                            ? 'rgba(74, 108, 247, 0.08)'
                            : 'rgba(99, 102, 241, 0.08)',
                        filter: 'blur(30px)',
                        animation: 'blobFloat 12s ease-in-out infinite alternate'
                    }} />

                    <Box sx={{
                        position: 'absolute',
                        bottom: '10%',
                        left: '15%',
                        width: 160,
                        height: 160,
                        borderRadius: '50%',
                        background: theme.palette.mode === 'dark'
                            ? 'rgba(74, 108, 247, 0.05)'
                            : 'rgba(99, 102, 241, 0.05)',
                        filter: 'blur(40px)',
                        animation: 'blobFloat 16s ease-in-out infinite alternate-reverse'
                    }} />

                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: '24px',
                            background: theme.palette.mode === 'dark'
                                ? 'rgba(26, 26, 46, 0.7)'
                                : 'rgba(248, 249, 255, 0.7)',
                            p: 4,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                            border: theme.palette.mode === 'dark'
                                ? '1px solid rgba(255, 255, 255, 0.08)'
                                : '1px solid rgba(0, 0, 0, 0.05)',
                            backdropFilter: 'blur(16px)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 4,
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(90deg, #4a6cf7, #3b5bdb)'
                                    : 'linear-gradient(90deg, #6366f1, #4f46e5)',
                                animation: 'liquidProgress 2s ease-out forwards'
                            },
                            animation: 'cardRise 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
                        }}
                    >
                        <CardContent>
                            <Box textAlign="center" sx={{ px: 2 }}>
                                {/* Liquid checkmark animation */}
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        position: 'relative',
                                        mb: 4,
                                        '& svg': {
                                            fontSize: 72,
                                            color: theme.palette.mode === 'dark'
                                                ? '#4a6cf7'
                                                : '#6366f1',
                                            animation: 'liquidFill 1.2s cubic-bezier(0.65, 0, 0.35, 1) forwards',
                                            '& path': {
                                                strokeDasharray: 100,
                                                strokeDashoffset: 100,
                                                animation: 'checkmarkDraw 1s ease-out 0.5s forwards'
                                            }
                                        }
                                    }}
                                >
                                    <svg width="0" height="0">
                                        <defs>
                                            <clipPath id="liquidClip">
                                                <path
                                                    d="M36 72C14.4 72 0 57.6 0 36S14.4 0 36 0s36 14.4 36 36-14.4 36-36 36z"
                                                    animation-name="morph"
                                                    animation-duration="4s"
                                                    animation-iteration-count="infinite"
                                                    animation-direction="alternate"
                                                    animation-timing-function="ease-in-out"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <CheckCircleOutlineRoundedIcon
                                        sx={{
                                            clipPath: 'url(#liquidClip)'
                                        }}
                                    />
                                </Box>

                                {/* Text with liquid rise effect */}
                                <Typography
                                    variant="h4"
                                    fontWeight={700}
                                    gutterBottom
                                    sx={{
                                        background: theme.palette.mode === 'dark'
                                            ? 'linear-gradient(180deg, #fff, #b8b8ff)'
                                            : 'linear-gradient(180deg, #000, #333)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        color: 'transparent',
                                        animation: 'textRise 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s forwards',
                                        opacity: 0,
                                        transform: 'translateY(20px)'
                                    }}
                                >
                                    Feedback Received
                                </Typography>

                                {/* Description with staggered words */}
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    mb={4}
                                    sx={{
                                        maxWidth: '480px',
                                        mx: 'auto',
                                        fontSize: '1.05rem',
                                        '& span': {
                                            display: 'inline-block',
                                            opacity: 0,
                                            animation: 'wordRise 0.6s ease-out forwards'
                                        }
                                    }}
                                >
                                    {["Thank you", "for sharing", "your thoughts!", "Your feedback", "has been", "successfully submitted."]
                                        .map((word, i) => (
                                            <span key={i} style={{ animationDelay: `${0.4 + i * 0.1}s` }}>
                                                {word}{' '}
                                            </span>
                                        ))
                                    }
                                </Typography>

                                {/* Liquid button */}
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/Dashboard')}
                                    sx={{
                                        px: 5,
                                        py: 1.8,
                                        borderRadius: '12px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        background: theme.palette.mode === 'dark'
                                            ? 'linear-gradient(135deg, #4a6cf7, #3b5bdb)'
                                            : 'linear-gradient(135deg, #6366f1, #4f46e5)',
                                        boxShadow: 'none',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transform: 'translateY(0)',
                                        animation: 'buttonAppear 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 1s forwards',
                                        opacity: 0,
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: '-50%',
                                            left: '-50%',
                                            width: '200%',
                                            height: '200%',
                                            background: 'rgba(255,255,255,0.1)',
                                            transform: 'rotate(45deg)',
                                            animation: 'liquidShine 3s ease-in-out infinite'
                                        },
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: theme.palette.mode === 'dark'
                                                ? '0 6px 20px rgba(74, 108, 247, 0.4)'
                                                : '0 6px 20px rgba(99, 102, 241, 0.4)'
                                        },
                                        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
                                    }}
                                >
                                    Return to Dashboard
                                </Button>

                                {/* Footer with fade */}
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mt={3}
                                    sx={{
                                        opacity: 0,
                                        animation: 'fadeIn 0.6s ease-out 1.4s forwards'
                                    }}
                                >
                                    Need to make changes? Contact support.
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Container>

                {/* Animation definitions */}
                <style jsx global>{`
                @keyframes liquidExpand {
                    0% { transform: scale(0.8); opacity: 0.3; }
                    100% { transform: scale(1.2); opacity: 0.1; }
                }
                
                @keyframes blobFloat {
                    0% { transform: translateY(0) rotate(0deg); border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%; }
                    50% { transform: translateY(-30px) rotate(5deg); border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
                    100% { transform: translateY(0) rotate(0deg); border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%; }
                }
                
                @keyframes cardRise {
                    0% { opacity: 0; transform: translateY(30px) scale(0.98); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                
                @keyframes liquidProgress {
                    0% { width: 0%; opacity: 1; }
                    100% { width: 100%; opacity: 1; }
                }
                
                @keyframes liquidFill {
                    0% { transform: scale(0); opacity: 0; }
                    80% { transform: scale(1.1); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                @keyframes checkmarkDraw {
                    0% { stroke-dashoffset: 100; }
                    100% { stroke-dashoffset: 0; }
                }
                
                @keyframes textRise {
                    0% { opacity: 0; transform: translateY(20px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes wordRise {
                    0% { opacity: 0; transform: translateY(10px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes buttonAppear {
                    0% { opacity: 0; transform: translateY(15px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes liquidShine {
                    0% { left: -100%; }
                    20% { left: 100%; }
                    100% { left: 100%; }
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 0.8; }
                }
                
                @keyframes morph {
                    0% { d: path('M36 72C14.4 72 0 57.6 0 36S14.4 0 36 0s36 14.4 36 36-14.4 36-36 36z'); }
                    50% { d: path('M36 72C20 72 0 52 0 36S20 0 36 0s36 20 36 36-20 36-36 36z'); }
                    100% { d: path('M36 72C14.4 72 0 57.6 0 36S14.4 0 36 0s36 14.4 36 36-14.4 36-36 36z'); }
                }
            `}</style>
            </>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                <Box textAlign="center">
                    <CircularProgress size={60} />
                    <Typography variant="h6" mt={2}>Loading feedback form...</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <>
            <Head />
           <Container maxWidth="md" sx={{ py: 2 }}>
                <Card elevation={0} sx={{
                    borderRadius: 4,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(to bottom, #1a1a2e, #16213e)'
                        : 'linear-gradient(to bottom, #f8f9ff, #eef2ff)',
                    boxShadow: theme.shadows[4],
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: theme.shadows[6]
                    }
                }}>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Stack spacing={3}>
                            <Box textAlign="center" sx={{ animation: 'fadeIn 0.5s ease' }}>
                                <Typography variant="h4" fontWeight={600} gutterBottom
                                    sx={{
                                        background: theme.palette.mode === 'dark'
                                            ? 'linear-gradient(45deg, #64b5f6, #2196f3)'
                                            : 'linear-gradient(45deg, #1976d2, #0d47a1)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        display: 'inline-block'
                                    }}>
                                    Course Feedback
                                </Typography>
                                <Typography color="text.secondary" variant="subtitle1">
                                    Please share your experience with us
                                </Typography>
                            </Box>

                            <Divider sx={{
                                my: 2,
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(to right, transparent, #64b5f6, transparent)'
                                    : 'linear-gradient(to right, transparent, #1976d2, transparent)',
                                height: 2
                            }} />

                            {questions.length === 0 ? (
                                <Box textAlign="center" py={6} sx={{ animation: 'fadeIn 0.8s ease' }}>
                                    <SentimentDissatisfiedIcon
                                        sx={{
                                            fontSize: 64,
                                            color: theme.palette.mode === 'dark' ? '#64b5f6' : '#1976d2',
                                            opacity: 0.8,
                                            mb: 2
                                        }}
                                    />
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                                        No Questions Available
                                    </Typography>
                                    <Typography color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                                        There are currently no feedback questions to answer. Please check back later or contact support if you believe this is an error.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ position: 'relative', pl: 4 }}>
                                        {questions.map((question, index) => (
                                            <Box
                                                key={question.id}
                                                sx={{
                                                    position: 'relative',
                                                    pb: 5,
                                                   
                                                    '&:not(:last-child)::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        left: 14,
                                                        top: 48,
                                                        bottom: 0,
                                                        width: 3,
                                                        bgcolor: ratings[question.id] ? 'primary.light' : 'divider',
                                                        transition: 'background-color 0.3s ease',
                                                    },
                                                }}
                                            >
                                                {/* Numbered Circle */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        top: 4,
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: '50%',
                                                        bgcolor: ratings[question.id]
                                                            ? 'primary.main'
                                                            : 'action.disabledBackground',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                        zIndex: 1,
                                                        border: `3px solid ${ratings[question.id]
                                                                ? theme.palette.primary.dark
                                                                : theme.palette.divider
                                                            }`,
                                                        transition: 'all 0.3s ease',
                                                    }}
                                                >
                                                    {index + 1}
                                                </Box>

                                                {/* Question & Rating */}
                                                <Box sx={{ ml: 5 }}>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={600}
                                                        gutterBottom
                                                        sx={{ fontSize: '1.25rem' }}
                                                    >
                                                        {question.questionText}
                                                    </Typography>

                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 3,
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        <Rating
                                                            name={`rating-${question.id}`}
                                                            value={ratings[question.id]}
                                                            onChange={(e, newValue) => {
                                                                setRatings({ ...ratings, [question.id]: newValue });
                                                            }}
                                                            size="large"
                                                            sx={{
                                                                fontSize: '2rem',
                                                                '& .MuiRating-iconFilled': {
                                                                    color: theme.palette.mode === 'dark'
                                                                        ? '#64b5f6'
                                                                        : '#1976d2',
                                                                },
                                                                '& .MuiRating-iconHover': {
                                                                    color: theme.palette.mode === 'dark'
                                                                        ? '#42a5f5'
                                                                        : '#1565c0',
                                                                },
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontSize: '1.1rem',
                                                                color: ratings[question.id]
                                                                    ? theme.palette.mode === 'dark'
                                                                        ? '#64b5f6'
                                                                        : '#1976d2'
                                                                    : 'text.secondary',
                                                                transition: 'color 0.3s ease',
                                                            }}
                                                        >
                                                            {ratings[question.id]
                                                                ? getRatingLabel(ratings[question.id])
                                                                : 'Select your rating'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>

                                    <Box
                                        display="flex"
                                        justifyContent="flex-end"
                                        mt={4}
                                        sx={{ animation: 'fadeInUp 0.5s ease' }}
                                    >
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={handleSubmit}
                                            disabled={Object.values(ratings).some(r => r === null)}
                                            sx={{
                                                px: 5,
                                                py: 1.5,
                                                borderRadius: 2,
                                                color: '#fff !important',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                fontSize: '1rem',
                                                background: theme.palette.mode === 'dark'
                                                    ? 'linear-gradient(45deg, #64b5f6, #1976d2)'
                                                    : 'linear-gradient(45deg, #1976d2, #0d47a1)',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: 4,
                                                    background: theme.palette.mode === 'dark'
                                                        ? 'linear-gradient(45deg, #42a5f5, #1565c0)'
                                                        : 'linear-gradient(45deg, #1565c0, #0b2c5a)'
                                                },
                                                '&.Mui-disabled': {
                                                    background: theme.palette.mode === 'dark'
                                                        ? '#424242'
                                                        : '#e0e0e0',
                                                    color: theme.palette.mode === 'dark'
                                                        ? '#757575'
                                                        : '#9e9e9e'
                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Submit Feedback
                                            <SendIcon sx={{ ml: 1, fontSize: 20 }} />
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            </Container>
        </>
    );
};

export default FeedbackForm;